import { ApolloProvider } from '@apollo/client/react';
import { dark, light } from '@servicetitan/anvil-themes';
import {
  ThemeProvider,
  usePrefersColorScheme,
} from '@servicetitan/design-system';
import { AnvilProvider } from '@servicetitan/anvil2';
import modules from '../modules';
import DiagnosticsProvider from '../modules/DiagnosticsModule/DiagnosticsProvider';
import appManager from './appManager';
import Router from './Router';
import { PortalRootProvider } from './PortalRootProvider';
import { getClient } from '@api/client';
import ToastsGroup from '@modules/ToastModule/components/ToastsGroup';
import SoftPhoneLoader from '@modules/SoftPhoneModule/components/SoftPhoneLoader';
import BugReportProvider from '@components/BugReportProvider/BugReportProvider';
import styles from './App.module.css';
import { ModalsGroup } from '@modules/ModalsModule/ModalsGroup';
import { HelpCenterMfeLoader } from '@modules/HelpCenterModule/components/HelpCenterMfeLoader';
import FeatureFlagModule from '@modules/FeatureFlagModule';
import { bindSharedLDService } from '@modules/FeatureFlagModule/lib/bindSharedLDService';
import { sharedLDService } from '@modules/FeatureFlagModule/lib/sharedLDService';
import KibanaModule from '@modules/KibanaModule';
import { bindSharedLog } from '@modules/KibanaModule/lib/bindSharedLog';
import {
  GlobalErrorBoundary,
  ModuleErrorBoundary,
} from '@components/ErrorBoundary';
import {
  UnreadTextCountMount,
  UnreadTextCountProvider,
} from '@modules/UnreadTextCountModule';

appManager.start(modules);
bindSharedLog(KibanaModule.log);
bindSharedLDService(sharedLDService);

const client = getClient();
window.client = client;

// expose globally so it can be accessed from cypress tests
Object.defineProperty(window, 'FeatureFlagModule', {
  value: FeatureFlagModule,
  writable: false,
  configurable: true, // just in case
});

function App() {
  const { theme } = usePrefersColorScheme(light, dark);

  return (
    <GlobalErrorBoundary>
      <AnvilProvider className={styles.AppContainer}>
        <PortalRootProvider>
          <ApolloProvider client={client}>
            <BugReportProvider>
              <ThemeProvider theme={theme}>
                <DiagnosticsProvider>
                  {/*
                    Wraps <Router /> so MessagesNavBadge can read the count, and the
                    hidden producer mount so the host can write it. Nothing else
                    needs it, and the underlying EventBus stays inside the mount.
                  */}
                  <UnreadTextCountProvider>
                    <Router />
                    <UnreadTextCountMount />
                  </UnreadTextCountProvider>
                  <ModuleErrorBoundary moduleName='ToastModule' size='compact'>
                    <ToastsGroup />
                  </ModuleErrorBoundary>
                  <ModuleErrorBoundary moduleName='ModalsGroup' size='compact'>
                    <ModalsGroup />
                  </ModuleErrorBoundary>
                  <ModuleErrorBoundary
                    moduleName='SoftPhoneModule'
                    size='compact'
                  >
                    <SoftPhoneLoader />
                  </ModuleErrorBoundary>
                  <ModuleErrorBoundary moduleName='HelpCenter' size='full'>
                    <HelpCenterMfeLoader />
                  </ModuleErrorBoundary>
                </DiagnosticsProvider>
              </ThemeProvider>
            </BugReportProvider>
          </ApolloProvider>
        </PortalRootProvider>
      </AnvilProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
